import { normalize } from "normalizr";

import { createAsyncActions } from "@ctra/utils";

import { CardPayload, CardResponse, LayoutResponse, PageResponse } from "./typings";
import schemas, { NormalizedLayout, NormalizedPage } from "./schemas";
import types from "./types";
import { FarmEntity, SiteEntity } from "../farms";

/**
 * Fetch saved chart cards
 * @type {AsyncActionCreator<(contextID: ("main" | number | string)) => {contextID: "main" | number | string}, (response: LayoutResponse) => NormalizedLayout, (error: string, statusCode: number) => {error: string, statusCode: number}>}
 */
const fetchSavedCharts = createAsyncActions(
  types.FETCH_SAVED_CHARTS,
  (contextID: "main" | FarmEntity["id"] | SiteEntity["id"]) => ({ contextID }),
  (
    contextID: "main" | FarmEntity["id"] | SiteEntity["id"],
    response: LayoutResponse
  ): NormalizedLayout & {
    contextID: "main" | FarmEntity["id"] | SiteEntity["id"];
  } => {
    return { contextID, ...normalize(response, schemas.layout) };
  },
  (contextID: "main" | FarmEntity["id"] | SiteEntity["id"], error: string, statusCode: number) => ({
    contextID,
    error,
    statusCode
  })
);

/**
 * Add new chart card
 * @type {AsyncActionCreator<(pageKey, groupID, groupName, cardPayload: CardPayload) => {pageKey: any, groupID: any, groupName: any, cardPayload: Omit<CardResponse, "id" | "layers"> & {layers: Array<LayerPayload>}}, (pageKey, groupID, newCard: PageResponse) => {pageKey: any, groupID: any, newCard: PageResponse}, (error: string) => {error: string}>}
 */
const addCard = createAsyncActions(
  types.ADD_CARD,
  (contextID, pageID, groupName, cardPayload: CardPayload) => ({
    contextID,
    pageID,
    groupName,
    cardPayload
  }),
  (contextID: string, pageID: string, cardResponse: CardResponse) => ({
    contextID,
    pageID,
    ...normalize(cardResponse, schemas.card)
  }),
  (error: string, statusCode: number) => ({ error, statusCode })
);

/**
 * Add a tab
 * @type {AsyncActionCreator<(pageKey, groupName) => {pageKey: any, groupName: any}, (pageKey, response: unknown) => {pageKey: any, response: unknown}, (error: string, statusCode: number) => {error: string, statusCode: number}>}
 */
const addTab = createAsyncActions(
  types.ADD_TAB,
  (contextID: string, groupName: string) => ({ contextID, groupName }),
  (contextID: string, response: PageResponse) => ({
    contextID,
    ...(normalize(response, schemas.page) as NormalizedPage)
  }),
  (error: string, statusCode: number) => ({ error, statusCode })
);

/**
 * Remove, add or reorder a tab/group
 * @type {AsyncActionCreator<(pageKey, pages) => {pageKey: any, pages: any}, (pageKey, pages) => {pageKey: any, pages: any}, (error: string, statusCode: number) => {error: string, statusCode: number}>}
 */
const updateTabs = createAsyncActions(
  types.UPDATE_TABS,
  (contextID: string, pages: Array<PageResponse>) => ({ contextID, pages }),
  (contextID: string, response: LayoutResponse) => ({ contextID, ...normalize(response, schemas.layout) }),
  (error: string, statusCode: number) => ({ error, statusCode })
);

/**
 * Update/reorder/delete charts
 * @type {AsyncActionCreator<(contextID: string, pages) => {contextID: string, pages: any}, (contextID: string, pages) => {contextID: string, pages: any}, (error: string, statusCode: number) => {error: string, statusCode: number}>}
 */
const updateCharts = createAsyncActions(
  types.UPDATE_CHARTS,
  (contextID: string, pages: Array<PageResponse>) => ({ contextID, pages }),
  (contextID: string, response: LayoutResponse) => ({ contextID, ...normalize(response, schemas.layout) }),
  (error: string, statusCode: number) => ({ error, statusCode })
);

export default {
  updateCharts,
  updateTabs,
  addTab,
  fetchSavedCharts,
  addCard
};
